// src/services/hrReportsService.js
// FuMind_GM HR - Reports & Analytics Service v1
// Uses same F_HR_Dashboard_Load unified flow endpoint as dashboard.

const HR_REPORTS_FLOW_URL = "/tracka-flow/v1/HR_REPORTS";
const HR_REPORTS_API_KEY = "";

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "object") return safeText(value.Value ?? value.value ?? value.Title ?? value.DisplayName ?? value.Email ?? "", fallback);
  return String(value).trim() || fallback;
}
function safeJson(value, fallback = null) {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return fallback; }
}
function asArray(value) {
  if (Array.isArray(value)) return value;
  const parsed = safeJson(value, []);
  return Array.isArray(parsed) ? parsed : [];
}
function asObject(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  const parsed = safeJson(value, {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
}
function num(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeReportsRole(role) {
  const x = String(role || "").toLowerCase().trim();
  if (x.includes("hradmin") || x === "hr admin") return "HRAdmin";
  if (x === "admin" || x.includes("administrator")) return "Admin";
  if (x === "hr") return "HR";
  if (x.includes("manager")) return "Manager";
  return "Employee";
}

export function normalizeReportsUser(user = {}) {
  const employeeId = user.employeeId || user.employeeCode || user.EmployeeID || user.EmployeeCode || "";
  const email = user.email || user.workEmail || user.Email || user.WorkEmail || "";
  return {
    employeeId,
    employeeCode: user.employeeCode || employeeId,
    workEmail: user.workEmail || email,
    email,
    displayName: user.displayName || user.name || user.employeeName || user.EmployeeName || employeeId || email || "Employee",
    role: normalizeReportsRole(user.role || user.Role || user.userRole || "Employee"),
  };
}

function cleanFilters(filters = {}) {
  return {
    countryCode: safeText(filters.countryCode),
    legalEntityCode: safeText(filters.legalEntityCode),
    locationCode: safeText(filters.locationCode),
    departmentCode: safeText(filters.departmentCode),
    workUnitCode: safeText(filters.workUnitCode),
    industryType: safeText(filters.industryType),
    employmentType: safeText(filters.employmentType),
  };
}

function normalizeFilterOptions(x = {}) {
  const readValues = (v) => asArray(v).map((row) => safeText(row.value ?? row)).filter(Boolean);
  return {
    countryCodes: readValues(x.countryCodes),
    legalEntityCodes: readValues(x.legalEntityCodes),
    locationCodes: readValues(x.locationCodes),
    departmentCodes: readValues(x.departmentCodes),
    workUnitCodes: readValues(x.workUnitCodes),
    industryTypes: readValues(x.industryTypes),
    employmentTypes: readValues(x.employmentTypes),
  };
}
function normalizeRows(rows = []) { return asArray(rows).map((x) => asObject(x)); }
function normalizePairs(rows = []) { return asArray(rows).map((x) => ({ label: safeText(x.label || x.name || x.key || x.value, "Not set"), count: num(x.count ?? x.value) })); }
function normalizeTrendRows(rows = []) { return asArray(rows).map((x, i) => ({ label: safeText(x.label || x.month || x.period, `P${i + 1}`), value: num(x.value ?? x.count) })); }

function normalizeCatalog(data = {}) {
  const raw = asObject(data.reportsData || data.dashboard || data.data || data);
  return {
    success: data.success !== false,
    reports: asArray(raw.reports),
    filterOptions: normalizeFilterOptions(asObject(raw.filterOptions)),
    raw,
  };
}

function normalizeAnalytics(data = {}) {
  const raw = asObject(data.analytics || data.reportsData || data.data || data);
  const dist = asObject(raw.distributions);
  const trends = asObject(raw.trends);
  return {
    success: data.success !== false,
    generatedOn: safeText(raw.generatedOn || data.generatedOn || new Date().toISOString()),
    reportType: safeText(raw.reportType),
    scope: safeText(raw.scope),
    appliedFilters: cleanFilters(raw.appliedFilters || raw.filters || {}),
    filterOptions: normalizeFilterOptions(asObject(raw.filterOptions)),
    kpis: {
      totalRecords: num(raw.kpis?.totalRecords),
      activeEmployees: num(raw.kpis?.activeEmployees),
      pendingItems: num(raw.kpis?.pendingItems),
      riskItems: num(raw.kpis?.riskItems),
      completionRate: num(raw.kpis?.completionRate),
    },
    distributions: { primary: normalizePairs(dist.primary), byCountry: normalizePairs(dist.byCountry), byDepartment: normalizePairs(dist.byDepartment) },
    trends: { primaryTrend: normalizeTrendRows(trends.primaryTrend), secondaryTrend: normalizeTrendRows(trends.secondaryTrend) },
    insights: normalizeRows(raw.insights || raw.risks),
    detailRows: normalizeRows(raw.detailRows),
    raw,
  };
}

async function postFlow(action, currentUser, payload = {}, normalize) {
  if (!HR_REPORTS_FLOW_URL || HR_REPORTS_FLOW_URL.includes("PASTE_")) {
    return normalize({ success: true, data: { generatedOn: new Date().toISOString(), filterOptions: {}, reports: [] } });
  }
  const res = await fetch(HR_REPORTS_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({  action, currentUser: normalizeReportsUser(currentUser), payload }),
  });
  const text = await res.text();
  const data = safeJson(text, { raw: text });
  if (!res.ok) throw new Error(data.message || data.error || text || `Reports flow failed (${res.status})`);
  if (data.success === false) throw new Error(data.message || "Reports flow returned failure.");
  return normalize(data);
}

export function loadHRReportCatalog({ currentUser, payload = {} }) {
  return postFlow("loadReportCatalog", currentUser, { scope: payload.scope || "org", filters: cleanFilters(payload.filters || {}) }, normalizeCatalog);
}

export function loadHRAnalyticsSummary({ currentUser, payload = {} }) {
  return postFlow("loadAnalyticsSummary", currentUser, {
    scope: payload.scope || "org",
    fromDate: payload.fromDate || "",
    toDate: payload.toDate || "",
    reportType: payload.reportType || "workforce",
    groupBy: payload.groupBy || "departmentCode",
    granularity: payload.granularity || "month",
    includeDetail: Boolean(payload.includeDetail),
    filters: cleanFilters(payload.filters || {}),
  }, normalizeAnalytics);
}

export function loadHRAnalyticsDetail({ currentUser, payload = {} }) {
  return postFlow("loadAnalyticsDetail", currentUser, {
    scope: payload.scope || "org",
    fromDate: payload.fromDate || "",
    toDate: payload.toDate || "",
    reportType: payload.reportType || "workforce",
    groupBy: payload.groupBy || "departmentCode",
    granularity: payload.granularity || "month",
    includeDetail: true,
    filters: cleanFilters(payload.filters || {}),
  }, normalizeAnalytics);
}
